import type { PortalSession } from '@pm/schemas';
import { Button, Field, Input } from '@pm/ui';
import { KeyRound, LogIn, Mail } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { BrandLogo, BrandMonogram } from '@/components/BrandMark';
import { ApiRequestError } from '@/lib/api';
import { hasLogo, recalledBrandOrg, usePublicBranding, useFavicon } from '@/lib/branding';
import { portalApi, usePortalAuth } from './portal-api';

function Frame({
  title,
  subtitle,
  invite,
  children,
}: {
  title: string;
  subtitle: string;
  /** Token do convite: identifica a organização antes do contato ter sessão. */
  invite?: string;
  children: React.ReactNode;
}) {
  const [rememberedOrg] = useState(() => recalledBrandOrg('portal'));
  const { data: brand } = usePublicBranding({ organization: rememberedOrg, invite });
  useFavicon(brand);
  return (
    <div
      className="grid min-h-screen place-items-center bg-bg px-4 text-text-1"
      style={{
        backgroundImage:
          'radial-gradient(700px 500px at 100% 0%, color-mix(in oklab, var(--accent) 10%, transparent), transparent 70%)',
      }}
    >
      <div className="w-full max-w-[400px] rounded-lg border border-border bg-surface-1 p-6">
        {hasLogo(brand) ? (
          <BrandLogo branding={brand} size="lg" className="mb-4" />
        ) : brand ? (
          <div className="mb-4 flex items-center gap-2">
            <BrandMonogram name={brand.display_name} className="size-7 text-xs" />
            <span className="truncate text-base font-semibold">{brand.display_name}</span>
          </div>
        ) : null}
        <p className="font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
          Portal do cliente
        </p>
        <h1 className="mt-1 text-xl font-medium">{title}</h1>
        <p className="mb-5 mt-1 text-sm text-text-2">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

export function PortalLoginPage() {
  const { me, signIn } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState<'idle' | 'sent'>('idle');
  if (me) return <Navigate to="/portal" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await portalApi.post<PortalSession>('/auth/login', { email, password });
      signIn(s);
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(
        from && from.startsWith('/portal') && from !== '/portal/login' ? from : '/portal',
        { replace: true },
      );
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.code === 'RATE_LIMITED'
            ? 'Muitas tentativas. Aguarde um minuto.'
            : 'E-mail ou senha inválidos.'
          : 'Não foi possível entrar.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function forgotPassword() {
    if (!email) return setError('Informe seu e-mail para receber o link.');
    try {
      await portalApi.post('/auth/forgot', { email });
      setForgot('sent');
    } catch {
      setForgot('sent');
    }
  }

  return (
    <Frame title="Entrar" subtitle="Acompanhe suas demandas, aprove entregas e envie solicitações.">
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3" noValidate>
        <Field label="E-mail" htmlFor="pl-email">
          <Input
            id="pl-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Senha" htmlFor="pl-pass">
          <Input
            id="pl-pass"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {forgot === 'sent' ? (
          <p className="text-sm text-success">
            Se este e-mail tiver acesso, você receberá um link para redefinir a senha.
          </p>
        ) : null}
        <Button type="submit" variant="primary" loading={busy} disabled={!email || !password}>
          <LogIn /> Entrar
        </Button>
        <button
          type="button"
          className="self-start text-xs text-text-3 hover:text-text-1"
          onClick={() => void forgotPassword()}
        >
          Esqueci minha senha
        </button>
      </form>
    </Frame>
  );
}

export function PortalInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const { signIn } = usePortalAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) return setError('A senha precisa ter ao menos 8 caracteres.');
    if (password !== confirm) return setError('As senhas não conferem.');
    setBusy(true);
    setError(null);
    try {
      const s = await portalApi.post<PortalSession>('/auth/accept-invite', { token, password });
      signIn(s);
      void navigate('/portal', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'PORTAL_INVITE_INVALID'
          ? 'Este convite é inválido ou expirou. Peça um novo link à equipe.'
          : 'Não foi possível concluir. Tente novamente.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame
      title="Defina sua senha"
      subtitle="Você foi convidado para acompanhar suas demandas no portal."
      invite={token}
    >
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3" noValidate>
        <Field label="Nova senha" htmlFor="pi-pass" hint="Mínimo de 8 caracteres.">
          <Input
            id="pi-pass"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Confirmar senha" htmlFor="pi-conf">
          <Input
            id="pi-conf"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <Button type="submit" variant="primary" loading={busy} disabled={!password || !confirm}>
          <KeyRound /> Salvar e entrar
        </Button>
        <Link
          to="/portal/login"
          className="inline-flex items-center gap-1 self-start text-xs text-text-3 hover:text-text-1"
        >
          <Mail className="size-3" /> Já tenho senha
        </Link>
      </form>
    </Frame>
  );
}
